import { Typography } from '@tunasong/ui-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { hasBlockDecorationPlugins, renderBlockDecorations, Transforms } from '@tunasong/plugin-lib'
import type { CoreElement } from '@tunasong/schemas'
import { hashColorName, Tag } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { isSystemTag } from '@tunasong/models'
import invariant from 'tiny-invariant'
import { ReactEditor } from 'slate-react'
import { isReactEditor } from '../editor/tuna-editor.js'
import styles from './block-decorations-bar.module.css'

interface BlockDecorationsBarProps {
  editor: TunaEditor
  element: CoreElement
}

export const BlockDecorationsBar: FC<BlockDecorationsBarProps> = ({ editor, element }) => {
  const hasDecorationPlugins = hasBlockDecorationPlugins(editor)
  const hasTags = element.tags && element.tags.some(tag => !isSystemTag(tag))
  const hasActiveView = Boolean(element.options?.activeView)

  if (!hasDecorationPlugins && !hasTags && !hasActiveView) {
    return null
  }

  const blockDecorations = hasDecorationPlugins ? renderBlockDecorations(editor, element) : []

  const singleTag = hasTags && element.tags?.filter(tag => !isSystemTag(tag)).length === 1 ? element.tags?.[0] ?? null : null
  const tagColor = singleTag ? hashColorName(singleTag) : null

  const handleDeleteTag = (tag: string) => {
    const newTags = element.tags?.filter(t => t !== tag) || []
    const newElement = { ...element, tags: newTags }
    invariant(editor, 'Editor must be defined to delete tag')
    invariant(isReactEditor(editor), 'Editor must be a ReactEditor to delete tag')

    const path = ReactEditor.findPath(editor, element)
    invariant(path, 'Element path must be found to delete tag')

    Transforms.setNodes(editor, newElement, { at: path })
  }

  if (blockDecorations.length === 0 && !singleTag && !hasActiveView) {
    return null
  }

  return (
    <div
      contentEditable={false}
      tabIndex={-1}
      className={styles.bar}
    >
      {singleTag && tagColor ? (
        <Tag label={singleTag} size={'small'} onDelete={() => handleDeleteTag(singleTag)} />
      ) : null}
      {hasActiveView ? (
        <Typography tabIndex={-1} variant="caption" color={'primary.main'} contentEditable={false}>
          View: {element.options?.activeView}
        </Typography>
      ) : null}
      {blockDecorations}
    </div>
  )
}
